import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable, inject } from "@angular/core";
import { Observable, shareReplay } from "rxjs";
import { map } from "rxjs/operators";
import { EnvConfig, Environment } from "@doc/core";
import { ApiUrls } from "../backoffice-urls";
import { ApiUrls as DocumentUrls } from "../document-urls";
import {
  ApplyAiSuggestionsRequest,
  DocumentAiChatRequest,
  DocumentAiChatResponse,
  DocumentAiQueryRequest,
  DocumentAiQueryResponse,
  DocumentAiOcr,
  DocumentAiResult,
  DocumentAiStatus,
  FileAiJob,
} from "../models/document-ai-process.model";

@Injectable({
  providedIn: "root",
})
export class AiServiceService {
  private http = inject(HttpClient);
  private apiUrl: string;
  private baseUrl: string;
  /** Keeps ngx-spinner off for poll / status checks (interceptor reads this). */
  private readonly skipLoaderOptions = {
    headers: new HttpHeaders({ 'X-Skip-Loader': 'true' }),
  };

  constructor(@Inject(EnvConfig) private config: Environment) {
    this.baseUrl = this.config.mockUrl;
    this.apiUrl = this.config.apiUrl;
  }

  CallAiOnApproval(id: any) {
    const link = this.apiUrl + ApiUrls.CallAiOnApproval;
    return this.http.post<any>(link, id);
  }

  SearchAndAnswerAi(id: any, isChat:boolean) {
    const url = isChat ? ApiUrls.SearchAndAnswerAi : ApiUrls.SearchAi
    const link = this.apiUrl + url;
    return this.http.post<any>(link, id);
  }

  DeleteAi(id: any) {
    const link = this.apiUrl + ApiUrls.DeleteAi;
    return this.http.post<any>(link, id);
  }

  
  private enabled$: Observable<boolean> | null = null;

  isEnabled(): Observable<boolean> {
    if (!this.enabled$) {
      this.enabled$ = this.http
        .get<any>(this.apiUrl + DocumentUrls.documentAiStatus, this.skipLoaderOptions)
        .pipe(
          map((res) => !!res?.data?.enabled),
          shareReplay(1)
        );
    }
    return this.enabled$;
  }

  
  processFile(fileId: number): Observable<number> {
    return this.http
      .post<any>(this.apiUrl + DocumentUrls.documentAiProcessFile + fileId, {})
      .pipe(map((res) => Number(res?.data?.documentProcessingId)));
  }

  /** Re-queue an existing job (retry after failure, or reprocess). */
  queue(documentProcessingId: number): Observable<any> {
    return this.http.post<any>(
      this.apiUrl + DocumentUrls.documentAiQueue + documentProcessingId,
      {}
    );
  }

  /**
   * Poll while processing runs. Cheap: no OCR text in the payload.
   * Returns a derived stage: queued | processing | completed | failed.
   */
  getProcessingStatus(documentProcessingId: number): Observable<DocumentAiStatus> {
    return this.http
      .get<any>(
        this.apiUrl + DocumentUrls.documentAiProcessingStatus + documentProcessingId,
        this.skipLoaderOptions
      )
      .pipe(map((res) => res?.data as DocumentAiStatus));
  }

  /**
   * Everything the review panel needs in ONE call: summary, segments,
   * extractions, pending suggestions, source context, classification
   * comparison and the manual/AI tag split.
   */
  getResult(documentProcessingId: number): Observable<DocumentAiResult> {
    return this.http
      .get<any>(
        this.apiUrl + DocumentUrls.documentAiResult + documentProcessingId,
        this.skipLoaderOptions
      )
      .pipe(map((res) => res?.data as DocumentAiResult));
  }

  /** Full extracted text (markdown) for the OCR viewer. */
  getOcr(documentProcessingId: number): Observable<DocumentAiOcr> {
    return this.http
      .get<any>(this.apiUrl + DocumentUrls.documentAiOcr + documentProcessingId)
      .pipe(map((res) => res?.data as DocumentAiOcr));
  }

  /** Accept or reject category / tag / sensitivity suggestions. */
  applySuggestions(request: ApplyAiSuggestionsRequest): Observable<any> {
    return this.http.post<any>(
      this.apiUrl + DocumentUrls.documentAiApplySuggestions,
      request
    );
  }

  /**
   * Remove an AI-generated tag. The backend refuses manual tags (403) - those
   * are removed through the normal tag screens.
   */
  removeAiTag(payload: {
    documentProcessingId: number;
    tagMappingId?: number | null;
    documentExtractionId?: number | null;
  }): Observable<any> {
    return this.http.post<any>(
      this.apiUrl + DocumentUrls.documentAiRemoveTag,
      payload
    );
  }

  /**
   * Ask a question against an indexed document. Proxies to dma-ai
   * POST /documents/chat (document_id + folderId).
   */
  chat(request: DocumentAiChatRequest): Observable<DocumentAiChatResponse> {
    return this.http
      .post<any>(
        this.apiUrl + DocumentUrls.documentAiChat,
        request,
        this.skipLoaderOptions
      )
      .pipe(
        map((res) => {
          const data = res?.data ?? res?.Data ?? {};
          return {
            question: data.question ?? data.Question ?? request.question,
            answer: data.answer ?? data.Answer ?? '',
            sources: data.sources ?? data.Sources ?? [],
          } as DocumentAiChatResponse;
        })
      );
  }

  /**
   * Semantic search against an indexed document. Proxies to dma-ai
   * POST /documents/query (document_id + folderId).
   */
  query(request: DocumentAiQueryRequest): Observable<DocumentAiQueryResponse> {
    return this.http
      .post<any>(
        this.apiUrl + DocumentUrls.documentAiQuery,
        request,
        this.skipLoaderOptions
      )
      .pipe(
        map((res) => {
          const data = res?.data ?? res?.Data ?? {};
          return {
            query: data.query ?? data.Query ?? request.query,
            answer: data.answer ?? data.Answer ?? '',
            results: data.results ?? data.Results ?? data.sources ?? data.Sources ?? [],
          } as DocumentAiQueryResponse;
        })
      );
  }

  /**
   * Batched job lookup for a list page - ONE request for all visible rows,
   * instead of a status call per row. Returns a fileId-keyed map; files never
   * processed are absent.
   */
  jobsFor(fileIds: number[]): Observable<Map<number, FileAiJob>> {
    const ids = (fileIds || []).filter((id) => Number.isFinite(id) && id > 0);
    return this.http
      .post<any>(
        this.apiUrl + DocumentUrls.documentAiJobsFor,
        { fileIds: ids },
        this.skipLoaderOptions
      )
      .pipe(
        map((res) => {
          const result = new Map<number, FileAiJob>();
          for (const job of (res?.data as FileAiJob[]) ?? []) {
            result.set(job.fileId, job);
          }
          return result;
        })
      );
  }
}
